import React, { useEffect, useMemo, useRef, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../../firebaseConfig";
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  Clock,
  Fingerprint,
  LogIn,
  LogOut,
  MapPin,
  RefreshCw,
  ShieldCheck,
  UserCheck,
} from "lucide-react";

const functions = getFunctions(auth.app, "asia-south1");
const verifyEmployee = httpsCallable(functions, "verifyAttendanceEmployee");
const markAttendance = httpsCallable(functions, "markEmployeeAttendance");

function getDeviceId() {
  const key = "shine_hrms_attendance_device_v1";
  let value = window.localStorage.getItem(key);
  if (!value) {
    value = window.crypto?.randomUUID?.() ||
      `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(key, value);
  }
  return value;
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Is browser me GPS available nahi hai."));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0,
    });
  });
}

function messageFromError(error) {
  const raw = error?.message || "Attendance request failed";
  return raw.replace(/^FirebaseError:\s*/i, "");
}

export default function EmployeeAttendanceKiosk() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const deviceId = useMemo(() => getDeviceId(), []);

  const [employeeCode, setEmployeeCode] = useState("");
  const [attendancePin, setAttendancePin] = useState("");
  const [employee, setEmployee] = useState(null);
  const [cameraStarted, setCameraStarted] = useState(false);
  const [locationInfo, setLocationInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => () => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
  }, []);

  const resetFeedback = () => {
    setMessage("");
    setError("");
  };

  const findEmployee = async () => {
    resetFeedback();
    if (!employeeCode.trim() || !/^\d{6}$/.test(attendancePin)) {
      setError("Employee Code aur 6-digit Attendance PIN required hai.");
      return;
    }
    setLoading(true);
    try {
      const response = await verifyEmployee({
        employeeCode: employeeCode.trim().toUpperCase(),
        pin: attendancePin,
        deviceId,
      });
      setEmployee(response.data.employee);
      setMessage("Employee identity verified.");
    } catch (err) {
      setEmployee(null);
      setError(messageFromError(err));
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    resetFeedback();
    try {
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setCameraStarted(true);
    } catch {
      setError("Camera permission allow karke page HTTPS par open karein.");
    }
  };

  const verifyLocation = async () => {
    resetFeedback();
    try {
      const position = await getPosition();
      const info = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: Math.round(position.coords.accuracy || 0),
      };
      setLocationInfo(info);
      setMessage("Live GPS captured. Final geofence verification server karega.");
      return info;
    } catch (err) {
      setError(err?.message || "Location permission allow karein.");
      throw err;
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !cameraStarted || video.readyState < 2) {
      throw new Error("Pehle live camera start karein.");
    }
    const sourceWidth = video.videoWidth || 1280;
    const sourceHeight = video.videoHeight || 720;
    const maxWidth = 960;
    const ratio = Math.min(1, maxWidth / sourceWidth);
    canvas.width = Math.round(sourceWidth * ratio);
    canvas.height = Math.round(sourceHeight * ratio);
    const context = canvas.getContext("2d", { alpha: false });
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.78);
  };

  const submitAttendance = async (actionType) => {
    resetFeedback();
    if (!employee) {
      setError("Pehle employee verify karein.");
      return;
    }
    if (!cameraStarted) {
      setError("Pehle live camera start karein.");
      return;
    }
    setLoading(true);
    try {
      const location = await verifyLocation();
      const photoDataUrl = capturePhoto();
      const response = await markAttendance({
        employeeCode: employeeCode.trim().toUpperCase(),
        pin: attendancePin,
        actionType,
        deviceId,
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        photoDataUrl,
      });
      setLocationInfo({
        ...location,
        distance: response.data.distanceMeters,
        insideOffice: response.data.insideOffice,
      });
      setMessage(
        `${actionType === "CHECK_IN" ? "Check-in" : "Check-out"} saved at ${
          response.data.serverTime
        }. Status: ${String(response.data.reviewStatus).replaceAll("_", " ")}.`
      );
    } catch (err) {
      setError(messageFromError(err));
    } finally {
      setLoading(false);
    }
  };

  const resetKiosk = () => {
    setEmployee(null);
    setEmployeeCode("");
    setAttendancePin("");
    setLocationInfo(null);
    resetFeedback();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-4 sm:p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 rounded-[2rem] border border-white/20 bg-white/10 p-6 text-white shadow-2xl">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="flex items-center gap-3 text-3xl font-black sm:text-4xl">
                <ShieldCheck className="text-cyan-300" /> Secure Attendance
              </h1>
              <p className="mt-2 text-blue-100">
                Server Time • Live Photo • GPS • Device Binding • Duplicate Lock
              </p>
            </div>
            <span className="rounded-full border border-emerald-300/30 bg-emerald-400/10 px-4 py-2 text-xs font-black uppercase tracking-widest text-emerald-200">
              Production Kiosk
            </span>
          </div>
        </div>

        {(message || error) && (
          <div className={`mb-5 flex items-start gap-3 rounded-2xl border p-4 font-semibold ${error ? "border-red-200 bg-red-50 text-red-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
            {error ? <AlertTriangle className="shrink-0" /> : <CheckCircle2 className="shrink-0" />}
            <p>{error || message}</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div className="space-y-5 xl:col-span-1">
            <div className="rounded-3xl bg-white p-6 shadow-2xl">
              <h2 className="mb-4 flex items-center gap-2 text-xl font-black">
                <UserCheck size={22} className="text-blue-600" /> Employee Verify
              </h2>
              <label className="block text-xs font-bold text-slate-600">Employee Code</label>
              <input className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold outline-none focus:border-blue-500" placeholder="SAAM-2026-XXXXXXX" value={employeeCode} onChange={(event) => setEmployeeCode(event.target.value.toUpperCase())} autoComplete="username" />
              <label className="mt-4 block text-xs font-bold text-slate-600">6-digit Attendance PIN</label>
              <input className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-3 font-semibold tracking-[.3em] outline-none focus:border-blue-500" type="password" inputMode="numeric" maxLength={6} placeholder="••••••" value={attendancePin} onChange={(event) => setAttendancePin(event.target.value.replace(/\D/g, "").slice(0, 6))} autoComplete="current-password" />
              <button type="button" onClick={findEmployee} disabled={loading} className="mt-4 w-full rounded-xl bg-blue-600 px-4 py-3 font-black text-white hover:bg-blue-700 disabled:opacity-50">
                {loading ? "Verifying..." : "Verify Employee"}
              </button>
              {employee && (
                <div className="mt-5 rounded-2xl border border-green-200 bg-green-50 p-4">
                  <p className="font-black text-green-800">{employee.name}</p>
                  <p className="mt-1 text-xs text-green-700">{employee.department} • {employee.designation}</p>
                  <p className="mt-2 flex items-center gap-1 text-[11px] font-bold text-green-700"><Fingerprint size={14} /> Device: {employee.deviceStatus}</p>
                </div>
              )}
            </div>

            <div className="rounded-3xl bg-white p-6 shadow-2xl">
              <h2 className="mb-4 flex items-center gap-2 text-xl font-black"><MapPin size={22} className="text-blue-600" /> Live Location</h2>
              <button type="button" onClick={verifyLocation} disabled={loading} className="w-full rounded-xl bg-emerald-600 px-4 py-3 font-black text-white hover:bg-emerald-700 disabled:opacity-50">Capture GPS</button>
              {locationInfo && (
                <div className={`mt-4 rounded-2xl border p-4 ${locationInfo.insideOffice === false ? "border-orange-200 bg-orange-50" : "border-blue-200 bg-blue-50"}`}>
                  <p className="font-black">Accuracy: {locationInfo.accuracy} metre</p>
                  {locationInfo.distance !== undefined && <p className="mt-1 text-sm">Office distance: {locationInfo.distance} metre</p>}
                </div>
              )}
            </div>

            <div className="rounded-3xl bg-white p-6 shadow-2xl">
              <h2 className="mb-4 flex items-center gap-2 text-xl font-black"><Clock size={22} className="text-blue-600" /> Attendance Action</h2>
              <button type="button" onClick={() => submitAttendance("CHECK_IN")} disabled={loading || !employee} className="mb-3 w-full rounded-xl bg-blue-600 px-4 py-3 font-black text-white hover:bg-blue-700 disabled:opacity-40"><LogIn size={18} className="mr-2 inline" /> Check In</button>
              <button type="button" onClick={() => submitAttendance("CHECK_OUT")} disabled={loading || !employee} className="w-full rounded-xl bg-emerald-600 px-4 py-3 font-black text-white hover:bg-emerald-700 disabled:opacity-40"><LogOut size={18} className="mr-2 inline" /> Check Out</button>
              <button type="button" onClick={resetKiosk} disabled={loading} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-800 px-4 py-3 font-black text-white"><RefreshCw size={17} /> Reset</button>
            </div>
          </div>

          <div className="rounded-[2rem] bg-white p-6 shadow-2xl xl:col-span-2">
            <h2 className="mb-5 flex items-center gap-2 text-2xl font-black"><Camera size={26} className="text-blue-600" /> Live Identity Photo</h2>
            {!cameraStarted && <button type="button" onClick={startCamera} className="mb-5 rounded-xl bg-blue-600 px-5 py-3 font-black text-white">Start Camera</button>}
            <div className="overflow-hidden rounded-3xl border bg-slate-900">
              <video ref={videoRef} autoPlay playsInline muted className="min-h-[420px] w-full object-cover" />
            </div>
            <canvas ref={canvasRef} className="hidden" />
            <div className="mt-5 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle className="shrink-0 text-amber-600" />
              <p className="text-sm font-semibold text-amber-800">
                Office ke bahar, low-accuracy GPS ya new device se attendance delete nahi hogi; PENDING HR REVIEW me jayegi. Duplicate check-in aur check-in ke bina check-out server reject karega.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
